import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ArrowRight, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Track-view dedupe (per browser session, per promo)
const VIEWED_KEY = 'govip_promo_viewed';
const getViewed = () => {
  try { return new Set(JSON.parse(sessionStorage.getItem(VIEWED_KEY) || '[]')); }
  catch { return new Set(); }
};
const saveViewed = (s) => {
  try { sessionStorage.setItem(VIEWED_KEY, JSON.stringify(Array.from(s))); } catch {}
};

const trackView = (id) => {
  const viewed = getViewed();
  if (viewed.has(id)) return;
  viewed.add(id);
  saveViewed(viewed);
  axios.post(`${API}/promos/${id}/view`).catch(() => {});
};

const HEADINGS = {
  pt: { kicker: 'Em Destaque', title: 'Experiências GO VIP', subtitle: 'Convites curados para os nossos próximos clientes premium.' },
  en: { kicker: 'Featured', title: 'GO VIP Experiences', subtitle: 'Hand-picked invitations for our next premium guests.' },
  es: { kicker: 'Destacados', title: 'Experiencias GO VIP', subtitle: 'Invitaciones cuidadas para nuestros próximos clientes premium.' },
  fr: { kicker: 'À la une', title: 'Expériences GO VIP', subtitle: 'Invitations soigneusement choisies pour nos clients premium.' },
};

const PromoCard = ({ promo, variant = 'feature', priority = 0 }) => {
  const ref = useRef(null);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackView(promo.id);
          io.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [promo.id]);

  const handleClick = (e) => {
    axios.post(`${API}/promos/${promo.id}/click`).catch(() => {});
    // Allow normal anchor behaviour to scroll to #booking
    if (promo.cta_link?.startsWith('#')) {
      e.preventDefault();
      const el = document.querySelector(promo.cta_link);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const isFeature = variant === 'feature';
  const heightClass = isFeature
    ? 'h-[460px] md:h-[560px] lg:h-[620px]'
    : 'h-[220px] md:h-[270px]';

  return (
    <a
      ref={ref}
      href={promo.cta_link || '#booking'}
      onClick={handleClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={`group relative block ${heightClass} w-full overflow-hidden rounded-2xl shadow-2xl ring-1 ring-amber-500/10 transition-transform duration-500 ease-out hover:scale-[1.015]`}
      style={{ animationDelay: `${priority * 90}ms` }}
      data-testid={`promo-card-${promo.id}`}
    >
      {/* Background image with slow Ken-Burns drift */}
      <img
        src={promo.image_data_url}
        alt={promo.title_text}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[6000ms] ease-out"
        style={{ transform: hover ? 'scale(1.08)' : 'scale(1.0)' }}
      />

      {/* Cinematic dark gradient — strong at bottom, fades to top */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/5" />

      {/* Subtle gold inner border on hover */}
      <div className={`pointer-events-none absolute inset-0 rounded-2xl ring-2 ring-amber-400/0 transition-all duration-500 ${hover ? 'ring-amber-400/70 shadow-[0_0_40px_-5px_rgba(245,158,11,0.5)]' : ''}`} />

      {/* FEATURED badge (top-left) */}
      {isFeature && (
        <div className="absolute left-5 top-5 flex items-center gap-1.5 rounded-full bg-amber-400/95 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-black shadow-lg backdrop-blur-sm">
          <Sparkles className="h-3 w-3" />
          {promo.platform === 'reel' ? 'Reel' : 'Featured'}
        </div>
      )}

      {/* Bottom content */}
      <div className={`absolute inset-x-0 bottom-0 p-5 md:p-7 ${isFeature ? 'md:p-9' : ''}`}>
        {promo.subtitle_text && (
          <p className={`mb-1.5 font-medium uppercase tracking-[0.18em] text-amber-300/90 ${isFeature ? 'text-xs md:text-sm' : 'text-[10px]'}`}>
            {promo.subtitle_text}
          </p>
        )}
        <h3
          className={`mb-3 font-serif font-bold leading-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)] ${
            isFeature ? 'text-2xl md:text-4xl lg:text-5xl' : 'text-lg md:text-xl'
          }`}
          data-testid={`promo-title-${promo.id}`}
        >
          {promo.title_text}
        </h3>

        <div className="inline-flex items-center gap-2 rounded-full bg-white/12 px-4 py-2 text-sm font-semibold text-white backdrop-blur-md ring-1 ring-white/25 transition-all duration-300 group-hover:bg-amber-500 group-hover:text-black group-hover:ring-amber-400">
          {promo.cta_label_text}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </div>
      </div>
    </a>
  );
};

export const FeaturedPromos = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').slice(0, 2);
  const [promos, setPromos] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const fetchPromos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/promos/active?lang=${lang}&limit=5`);
      setPromos(res.data.promos || []);
    } catch (e) {
      // Silent: section is optional
    } finally {
      setLoaded(true);
    }
  }, [lang]);

  useEffect(() => { fetchPromos(); }, [fetchPromos]);

  if (!loaded || promos.length === 0) return null;

  const heading = HEADINGS[lang] || HEADINGS.en;
  const [hero, ...rest] = promos;

  return (
    <section
      id="featured-promos"
      className="relative overflow-hidden bg-gradient-to-b from-[#0a0a0a] via-[#111] to-[#0a0a0a] py-20 md:py-28"
      data-testid="featured-promos-section"
    >
      {/* Decorative gold radial glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 h-[600px] w-[600px] -translate-x-1/2 rounded-full opacity-30 blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.45), transparent 70%)' }}
      />
      {/* Subtle grain overlay */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
        }}
      />

      <div className="container relative mx-auto max-w-7xl px-4">
        {/* Heading — left aligned, asymmetric */}
        <div className="mb-10 md:mb-14 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-amber-400">
              <span className="h-px w-8 bg-amber-400/60" />
              {heading.kicker}
            </div>
            <h2 className="font-serif text-3xl font-bold leading-tight text-white md:text-5xl lg:text-6xl">
              {heading.title}
            </h2>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-zinc-400 md:text-base">
            {heading.subtitle}
          </p>
        </div>

        {/* Asymmetric grid: hero (left, 2/3) + stack of 2 (right, 1/3) */}
        <div className="grid grid-cols-1 gap-5 md:gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <PromoCard promo={hero} variant="feature" priority={0} />
          </div>

          {rest.length > 0 && (
            <div className="flex flex-col gap-5 md:gap-6">
              {rest.slice(0, 2).map((p, i) => (
                <PromoCard key={p.id} promo={p} variant="small" priority={i + 1} />
              ))}
            </div>
          )}
        </div>

        {/* Extra row if there are 4+ promos */}
        {rest.length > 2 && (
          <div className="mt-5 md:mt-6 grid grid-cols-1 gap-5 md:gap-6 md:grid-cols-2 lg:grid-cols-3">
            {rest.slice(2, 5).map((p, i) => (
              <PromoCard key={p.id} promo={p} variant="small" priority={i + 3} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedPromos;
